import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { StudentFilter } from '@tyro/api';
import { Autocomplete, type AutocompleteProps } from '@tyro/core/autocomplete';
import { Avatar } from '@tyro/core/avatar';
import { RHFAutocomplete, type RHFAutocompleteProps } from '@tyro/core/form';
import { usePreferredNameLayout } from '@tyro/core/hooks';
import {
  type CustomCellEditorProps,
  TableAutocomplete,
} from '@tyro/core/table';
import { useTranslation } from '@tyro/i18n';
import type { FieldValues } from 'react-hook-form';
import {
  type StudentSelectOption,
  useStudentsForSelect,
} from '../../api/student/students';
import { usePeopleAutocompleteProps } from './use-people-autocomplete-props';

type RHFStudentAutocompleteProps<TField extends FieldValues> = Omit<
  RHFAutocompleteProps<TField, StudentSelectOption>,
  'options'
> & { hideAvatar?: boolean; filter?: StudentFilter };

type StudentAutocompleteProps = Omit<
  AutocompleteProps<StudentSelectOption>,
  'options'
> & { hideAvatar?: boolean; filter?: StudentFilter };

export const RHFStudentAutocomplete = <TField extends FieldValues>({
  hideAvatar,
  filter = {},
  ...props
}: RHFStudentAutocompleteProps<TField>) => {
  const { t } = useTranslation(['common']);
  const { data: studentData, isLoading } = useStudentsForSelect(filter);
  const peopleAutocompleteProps =
    usePeopleAutocompleteProps<StudentSelectOption>({ hideAvatar });

  return (
    <RHFAutocomplete<TField, StudentSelectOption>
      label={t('common:student')}
      {...peopleAutocompleteProps}
      fullWidth
      loading={isLoading}
      options={studentData ?? []}
      {...props}
    />
  );
};

export const StudentAutocomplete = ({
  hideAvatar,
  filter = {},
  ...props
}: StudentAutocompleteProps) => {
  const { t } = useTranslation(['common']);
  const { data: studentData, isLoading } = useStudentsForSelect(filter);
  const peopleAutocompleteProps =
    usePeopleAutocompleteProps<StudentSelectOption>({ hideAvatar });

  return (
    <Autocomplete
      label={t('common:student')}
      {...peopleAutocompleteProps}
      fullWidth
      optionIdKey="partyId"
      loading={isLoading}
      options={studentData ?? []}
      {...props}
    />
  );
};

export function TableStudentAutocomplete(
  props: CustomCellEditorProps<unknown, StudentSelectOption | null>,
) {
  const { t } = useTranslation(['common']);
  const { data, isLoading } = useStudentsForSelect({});
  const { displayName } = usePreferredNameLayout();

  const { getOptionLabel, optionIdKey, filterOptions } =
    usePeopleAutocompleteProps<StudentSelectOption>();

  return (
    <TableAutocomplete<StudentSelectOption | null>
      {...props}
      value={
        Array.isArray(props.value) && props.value.length > 0
          ? props.value[0]
          : null
      }
      onValueChange={(newValue) => {
        const typedValue = newValue as StudentSelectOption;
        props.onValueChange(typedValue);
      }}
      options={data ?? []}
      getOptionLabel={(option) =>
        option && getOptionLabel ? getOptionLabel(option) : ''
      }
      optionIdKey={optionIdKey}
      AutocompleteProps={{
        // @ts-expect-error
        filterOptions,
        autoHighlight: true,
        loading: isLoading,
        loadingText: t('common:loading'),
        renderOption: ({ key, ...optionProps }, option) => {
          const name = displayName(option);
          return (
            <Stack
              key={key}
              component="li"
              direction="row"
              spacing={1}
              {...optionProps}
            >
              <Avatar
                name={name}
                src={option?.avatarUrl}
                person={option}
                sx={{ width: 32, height: 32, fontSize: '0.75rem' }}
              />
              <Stack>
                <Typography variant="subtitle2">{name}</Typography>
              </Stack>
            </Stack>
          );
        },
      }}
    />
  );
}
